import {
    color2HexAndAlpha,
    getColorRGB,
    hexAndAlpha2Color, hexToRgba, rgb2Hex,
} from '@src/slide/infrastructure/module/independent/color/transform';
import { DeepPartial, DeepReadonly } from 'ts-essentials';
import { ICTSRgbColor } from '@tencent/slide-sdk-model';
import { STSchemeColorVal } from '@tencent/slide-sdk-pb-model/src/ultrabuf/ultrabuf.types';

export type ITDColorAttr = Omit<ITDColor
    , 'toPropValue' | 'updateCustomColor'
    | 'updateOpacity' | 'getColorWithNoAlpha' | 'srgbColor' | 'originAlpha' | 'hsl' | 'isSchemeColor'>;

export interface IPropValue {
    toPropValue(): any;
}

// todo: 这三个方法放到基础设施里
/**
 * 将alpha通道值转换成对应的透明度百分比
 * @param {number} alpha 小数，alpha通道，单位不是emu
 */
export const alphaToOpacity = (alpha: number) => Math.round((1 - alpha) * 100);

/**
 * 将UI上的透明度百分比转换成图形、图片等可以接受的alpha通道，单位不是emu
 * @param {number} opacity UI上显示的透明度百分比
 */
export const opacityToAlpha = (opacity: number) => {
    if (opacity > 100) {
        opacity = 100;
    }
    return Number((1 - (opacity / 100)).toFixed(2));
};

export function rgbaToHexA(rgba) {
    const result = rgba.match(/^rgba?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+(\.\d)?)[\s+]?/i);
    return (result && result.length >= 4)
        ? {
            hex: (`#${
                (`0${parseInt(result[1], 10).toString(16)}`).slice(-2)
            }${(`0${parseInt(result[2], 10).toString(16)}`).slice(-2)
            }${(`0${parseInt(result[3], 10).toString(16)}`).slice(-2)}`).toUpperCase(),
            o: +result[4],
        } : rgba;
}

export interface ITDColor extends IPropValue {
    // todo: 改成一个枚举：纯色、渐变。。
    colorType: string;
    /**
     * 色值，可能是自定义色、主题key，是不带各种颜色转换的原始色值/key
     */
    colorValue: string | STSchemeColorVal;
    /**
     * 透明度
     */
    alpha: number;
    /**
     * 发光1
     */
    lumMod: any;
    /**
     * 发光2
     */
    lumOff: any;
    /**
     * 加黑
     */
    shade: any;
    /**
     * 加白
     */
    tint: any;
    hsl: IHSL;
    /**
     * 计算/继承后，ui渲染的色值，这个是经过颜色转换的8位色值，一般ui直接用这个渲染，之后看看能不能把8位改成6位
     * 格式为 #000000ff
     */
    srgb_color: string;
    srgbColor: string;
    originAlpha: number;
    isSchemeColor: boolean;
    rgba: string;
    rgb: string;

    /**
     * 更新色值
     * @param color
     */
    updateCustomColor(color: string);
    updateOpacity(opacity: number);
    getColorWithNoAlpha(): string;


    /**
     * 更新主题色 todo
     */
    // updateTheme();
}

export interface ITDParams {
    rgbaStr?: string; // 颜色字符串，例如 #FFF, #FFFFFF, rgba(255,255,255,1)
    srgbClr?: DeepReadonly<DeepPartial<ICTSRgbColor>> | null; // 3.0的色值对象
    lumOff?: number;
    lumMod?: number;
    shade?: number;
    tint?: number;
    alpha?: number;
    themeKey?: STSchemeColorVal;
}

export interface IRGB {
    R: number;
    G: number;
    B: number;
}

export interface IHSL {
    H: number;
    L: number;
    S: number;
}

export class TDColor implements ITDColor {
    public static hue2RGB(v1: number, v2: number, h: number) {
        let vH = h;
        if (vH < 0) vH += 1;
        if (vH > 1) vH -= 1;
        if (6.0 * vH < 1) return v1 + ((v2 - v1) * 6.0 * vH);
        if (2.0 * vH < 1) return v2;
        if (3.0 * vH < 2) return v1 + ((v2 - v1) * ((2.0 / 3.0) - vH) * 6.0);
        return v1;
    }

    public static number2Hex(num: number) {
        // 标准色存的是大写
        const hex = num.toString(16).toUpperCase();
        return hex.length === 1 ? `0${hex}` : hex;
    }

    public static rgb2hexStrWithRgbObj(r: number, g: number, b: number) {
        return `#${TDColor.number2Hex(r)}${TDColor.number2Hex(g)}${TDColor.number2Hex(b)}`;
    }

    public static hsl2Rgb(hsl: IHSL) {
        let R = 0;
        let G = 0;
        let B = 0;
        const H = (hsl.H * 1.0) / 360;
        const S = (hsl.S * 1.0) / 255;
        const L = (hsl.L * 1.0) / 255;
        let v1;
        let v2;
        if (S === 0) {
            // HSL values = 0 ÷ 1
            R = L * 255.0; // RGB results = 0 ÷ 255
            G = L * 255.0;
            B = L * 255.0;
        } else {
            if (L < 0.5) v2 = L * (1 + S);
            else v2 = L + S - (S * L);

            v1 = (2.0 * L) - v2;

            R = 255.0 * TDColor.hue2RGB(v1, v2, H + (1.0 / 3.0));
            G = 255.0 * TDColor.hue2RGB(v1, v2, H);
            B = 255.0 * TDColor.hue2RGB(v1, v2, H - (1.0 / 3.0));
        }
        return { R: Math.floor(R + 0.5), G: Math.floor(G + 0.5), B: Math.floor(B + 0.5) };
    }

    public static rgb2RgbString(rgb: { R: number, G: number, B: number}) {
        const { R, G, B } = rgb;
        return `rgb(${R}, ${G}, ${B})`;
    }

    public static hex2RgbObject(hex: string): IRGB | null {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            R: parseInt(result[1], 16),
            G: parseInt(result[2], 16),
            B: parseInt(result[3], 16),
        } : null;
    }

    public static lumToHex(hex: string, lumOff = 0, lumMod = 1e5): string {
        const hex1 = TDColor.luminanceWithMod(hex, lumMod / 100000);
        return TDColor.luminanceWithOff(hex1, lumOff / 100000);
    }

    public static luminanceWithMod(hex: string, lumMod = 1): string {
        const rgb = TDColor.hex2RgbObject(hex);
        if (!rgb) {
            console.info('[lumToHex]: hex %s', hex);
            throw new Error('lum To hex fail');
        }

        const hsl = TDColor.rgb2Hsl(rgb);
        const newLum = (hsl.L * lumMod);
        hsl.L = Math.min(newLum, 255);
        const newRgb = TDColor.hsl2Rgb(hsl);

        return TDColor.rgb2hexStrWithRgbObj(newRgb.R, newRgb.G, newRgb.B);
    }

    public static luminanceWithOff(hex: string, lumOff = 0): string {
        const rgb = TDColor.hex2RgbObject(hex);
        if (!rgb) {
            console.info('[lumToHex]: hex %s', hex);
            throw new Error('lum To hex fail');
        }

        const hsl = TDColor.rgb2Hsl(rgb);
        const newLum = (hsl.L + (255 * lumOff));
        hsl.L = Math.min(newLum, 255);
        const newRgb = TDColor.hsl2Rgb(hsl);

        return TDColor.rgb2hexStrWithRgbObj(newRgb.R, newRgb.G, newRgb.B);
    }

    public static rgb2Hsl(rgb: IRGB): IHSL {
        const R = rgb.R / 255.0; // Where RGB values = 0 ÷ 255
        const G = rgb.G / 255.0;
        const B = rgb.B / 255.0;
        const Min = Math.min(R, Math.min(G, B)); // Min. value of RGB
        const Max = Math.max(R, Math.max(G, B)); // Max. value of RGB
        const delMax = Max - Min; // Delta RGB value

        let H = 0;
        let S = 0;
        let L = 0;
        let delR;
        let delG;
        let delB;

        L = (Max + Min) / 2.0;
        if (Math.abs(delMax) < 1e-6) {
            // This is a gray, no chroma...
            // H = 2.0/3.0;          //Windows下S值为0时，H值始终为160（2/3*240）
            H = 0; // HSL results = 0 ÷ 1
            S = 0;
        } else {
            // Chromatic data...
            if (L < 0.5) S = delMax / (Max + Min);
            else S = delMax / (2 - Max - Min);

            delR = (((Max - R) / 6.0) + (delMax / 2.0)) / delMax;
            delG = (((Max - G) / 6.0) + (delMax / 2.0)) / delMax;
            delB = (((Max - B) / 6.0) + (delMax / 2.0)) / delMax;

            if (Math.abs(R - Max) < 1e-6) H = delB - delG;
            else if (Math.abs(G - Max) < 1e-6) H = (1.0 / 3.0) + delR - delB;
            else if (Math.abs(B - Max) < 1e-6) H = (2.0 / 3.0) + delG - delR;

            if (H < 0) H += 1;
            if (H > 1) H -= 1;
        }

        return { H: H * 360, S: S * 255, L: L * 255 };
    }

    /**
     * 格式化各种颜色
     * 从ooxml的角度来说，所有色值都是不带#的16进制6位色值，colorValue对应就是ooxml数据
     * 对于ui来说，目前多数组件用的都是带#的16进制8位色值，下一步要把8位色值替换成颜色对象
     * 但是对于css来说，又需要rgba来表示颜色，所以对各种情况的色值进行格式化
     * @param color
     * @private
     */
    public static formatColor(color: string) {
        if (color?.startsWith('rgba')) {
            const { hex, o } = rgbaToHexA(color);
            const str = hexAndAlpha2Color(hex, o).replace?.('##', '#');
            return str;
        } if (color?.startsWith('rgb')) {
            return `${rgb2Hex(color)}ff`;
        } if (color?.startsWith('#')) {
            return color;
        }
        return `#${color}`.toLowerCase();
    }

    public static hexToIRgb(hex: string): IRGB {
        const [R, G, B] = hex.match(/\w\w/g)?.map(x => parseInt(x, 16)) || [];
        return {
            R,
            G,
            B,
        };
    }

    public static rgbOrRgbaToRgbaWithNewAlpha(rgbValue: string, alpha = 1) {
        const parseStr = rgbValue.replace('a', '');
        const rgb = /rgb\((\d+,\s*[\d]+,\s*[\d]+)/g.exec(parseStr);
        return rgb ? `rgba(${rgb[1]},${alpha})` : rgbValue;
    }

    public static getOpacityChangedColor(opacity: number, color: string) {
        const alpha = opacityToAlpha(opacity) ?? 1;
        const rgb = getColorRGB(color);
        return `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${alpha})`;
    }

    public static rgbaToRgb(rgba) {
        if (!rgba) return '';
        const result = rgba.match(/^rgba?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+(\.\d)?)[\s+]?/i);
        return `rgb(${result[1]}, ${result[2]}, ${result[3]})`;
    }

    public colorType = 'rgb';
    public colorValue: string | STSchemeColorVal = 'FFFFFF';
    public lumMod: any = null;
    public lumOff: any = null;
    public shade: any = null;
    public tint: any = null;
    // todo: 改驼峰，srgbColor是一个8位的16进制色值，直接用于渲染ui
    // todo: 去除8位色值，改6位，后两位直接用alpha
    public srgbColor = '';
    public originAlpha = 1;

    /**
     * 创建TDColor对象
     * @param params
     */
    // eslint-disable-next-line complexity
    public constructor(params?: ITDParams) {
        if (!params) return;
        const { rgbaStr, srgbClr, themeKey, lumOff, lumMod, alpha, shade, tint } = params;
        if (themeKey) {
            this.colorValue = themeKey;
            if (rgbaStr) {
                const cv = color2HexAndAlpha(rgbaStr);
                this.srgb_color = hexAndAlpha2Color(cv.colorValue, this.alpha);
                this.alpha = Math.min(Math.max(cv.alpha, 0), 1);
            } else {
                this.alpha = 1;
            }
            this.lumOff = lumOff;
            this.lumMod = lumMod;
            this.shade = shade;
            this.tint = tint;
        } else if (rgbaStr && rgbaStr.length > 0) {
            const cv = color2HexAndAlpha(rgbaStr);
            this.colorValue = cv.colorValue;
            this.alpha = Math.min(Math.max(cv.alpha, 0), 1);
            this.srgb_color = hexAndAlpha2Color(this.colorValue, this.alpha);
            this.lumOff = lumOff;
            this.lumMod = lumMod;
            this.shade = shade;
            this.tint = tint;
        } else if (srgbClr) {
            this.colorValue = srgbClr?.val ?? '';
            this.alpha = (srgbClr?.egColorTransform?.[0]?.alpha?.val ?? 100000) / 100000;
            this.srgb_color = hexAndAlpha2Color(this.colorValue, this.alpha);
        }
        if (alpha ?? Number.isSafeInteger(alpha)) {
            this.alpha = alpha || 1;
        }
    }

    public get rgba(): string {
        return hexToRgba(this.srgb_color, this.alpha);
    }

    public get rgb(): string {
        return TDColor.rgbaToRgb(this.rgba);
    }

    public set alpha(alpha: number) {
        if (alpha > 1) alpha = 1;
        this.originAlpha = alpha;
        if (this.srgb_color) {
            this.srgb_color = hexAndAlpha2Color(this.srgb_color.slice(0, 7), alpha);
        }
    }

    public get alpha() {
        return this.originAlpha;
    }

    public set srgb_color(val: string) {
        this.srgbColor = val?.toLowerCase?.();
    }
    public get srgb_color() {
        return this.srgbColor;
    }

    public get isSchemeColor(): boolean {
        return !!STSchemeColorVal[this.colorValue];
    }

    public get hsl(): IHSL {
        return TDColor.rgb2Hsl(TDColor.hexToIRgb(this.srgb_color));
    }

    public toPropValue(): {
        color: ITDColorAttr;
    } {
        return {
            color: {
                alpha: this.alpha,
                colorType: this.colorType,
                colorValue: this.colorValue,
                lumMod: this.lumMod,
                lumOff: this.lumOff,
                shade: this.shade,
                tint: this.tint,
                srgb_color: this.srgb_color,
                rgba: this.rgba,
                rgb: this.rgb,
            },
        };
    }

    public updateCustomColor(props: string) {
        const color = TDColor.formatColor(props);
        const opacity = alphaToOpacity(this.alpha);
        const originHex = (rgbaToHexA(color) as string).slice(0, 7);
        this.colorValue = originHex.replace('#', '');
        this.srgb_color = TDColor.formatColor(TDColor.getOpacityChangedColor(opacity, originHex));
    }

    public updateOpacity(opacity: number) {
        this.alpha = opacityToAlpha(opacity);
    }

    public getColorWithNoAlpha() {
        return this.srgb_color.slice(0, this.srgb_color.length - 2);
    }
}
